import React, { useRef, useState } from "react";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import {
  Download, Smartphone, Monitor, Image as ImageIcon,
  Link as LinkIcon, Twitter, MessageCircle, AtSign, Copy,
} from "lucide-react";
import OperativeCard from "./OperativeCard";
import ResponsiveCardFrame from "./ResponsiveCardFrame";

/**
 * CardActions — wraps the OperativeCard with download + share buttons.
 *
 * All exports render the SAME OperativeCard component to an offscreen scaled
 * container, then html-to-image captures it. This guarantees the card never
 * drifts from its on-screen preview.
 */

const _btnBase = {
  display: "inline-flex", alignItems: "center", gap: 6,
  padding: "8px 12px",
  fontFamily: "ui-monospace, monospace",
  fontSize: 10, letterSpacing: "0.3em",
  border: "1px solid #454038",
  color: "#E4DFD3",
  background: "rgba(0,0,0,0.4)",
  cursor: "pointer",
  textTransform: "uppercase",
};

const Btn = ({ icon: Icon, children, onClick, testid, accent }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    style={{ ..._btnBase, borderColor: accent || "#454038", color: accent || "#E4DFD3" }}
  >
    {Icon ? <Icon size={11} /> : null}
    {children}
  </button>
);

const _download = (dataUrl, filename) => {
  const link = document.createElement("a");
  link.download = filename;
  link.href = dataUrl;
  link.click();
};

const _renderToPng = async (node, { scale = 2, bg = null } = {}) => {
  return toPng(node, {
    pixelRatio: scale,
    cacheBust: true,
    backgroundColor: bg || undefined,
    skipFonts: false,
  });
};

const CardActions = ({ card, qrUrl }) => {
  const cardRef = useRef(null);
  const phoneRef = useRef(null);
  const desktopRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const safeSerial = (card?.archive_serial || "archive").replace(/[^A-Z0-9-]/gi, "");

  const exportStandard = async (scale, suffix) => {
    if (!cardRef.current || busy) return;
    setBusy(true);
    try {
      const url = await _renderToPng(cardRef.current, { scale });
      _download(url, `archive-operative-${safeSerial}${suffix}.png`);
      toast.success(`Downloaded ${suffix.replace(/[._-]/g, " ").trim() || "card"}`);
    } catch (e) {
      toast.error("Download failed");
      console.error(e);
    } finally { setBusy(false); }
  };

  const exportWallpaper = async (ref, label) => {
    if (!ref.current || busy) return;
    setBusy(true);
    try {
      const url = await _renderToPng(ref.current, { scale: 2 });
      _download(url, `archive-operative-${safeSerial}-${label}.png`);
      toast.success(`Downloaded ${label} wallpaper`);
    } catch (e) {
      toast.error("Wallpaper export failed");
      console.error(e);
    } finally { setBusy(false); }
  };

  const publicUrl = card?.public_profile_url
    ? `${window.location.origin}${card.public_profile_url}`
    : null;

  const copy = async (txt, label) => {
    try {
      await navigator.clipboard.writeText(txt);
      toast.success(`${label} copied`);
    } catch {
      toast.error("Copy failed");
    }
  };

  const shareIntent = (url) => window.open(url, "_blank", "noopener,noreferrer");

  const shareText = card
    ? `Operative ${card.callsign} · ${card.clearance?.label} · ${card.archive_serial}\n\nMy credential at The Archive.`
    : "";

  const xUrl = publicUrl
    ? `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(publicUrl)}`
    : null;
  const redditUrl = publicUrl
    ? `https://reddit.com/submit?url=${encodeURIComponent(publicUrl)}&title=${encodeURIComponent(`Operative ${card?.callsign} · The Archive`)}`
    : null;
  const bskyUrl = publicUrl
    ? `https://bsky.app/intent/compose?text=${encodeURIComponent(shareText + "\n\n" + publicUrl)}`
    : null;

  return (
    <div data-testid="card-actions" style={{ display: "flex", flexDirection: "column", gap: 24, alignItems: "center", width: "100%" }}>
      {/* On-screen card — scales down to fit any viewport while underlying
          DOM stays at native 650×1000 so PNG exports remain full-resolution. */}
      <ResponsiveCardFrame data-testid="card-frame">
        <OperativeCard ref={cardRef} card={card} qrUrl={publicUrl} />
      </ResponsiveCardFrame>

      {/* Action grid */}
      <div style={{ width: "100%", maxWidth: 650 }}>
        <div style={{
          fontFamily: "ui-monospace, monospace", fontSize: 9, letterSpacing: "0.4em",
          color: "#6F6A60", marginBottom: 8,
        }}>
          // DOWNLOADS
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          <Btn icon={ImageIcon}  onClick={() => exportStandard(2, ".png")}    testid="download-png">PNG</Btn>
          <Btn icon={Download}   onClick={() => exportStandard(4, "-hires.png")} testid="download-hires">HI-RES PNG</Btn>
          <Btn icon={Smartphone} onClick={() => exportWallpaper(phoneRef, "phone")}   testid="download-phone">PHONE WALLPAPER</Btn>
          <Btn icon={Monitor}    onClick={() => exportWallpaper(desktopRef, "desktop")} testid="download-desktop">DESKTOP WALLPAPER</Btn>
        </div>

        <div style={{
          fontFamily: "ui-monospace, monospace", fontSize: 9, letterSpacing: "0.4em",
          color: "#6F6A60", margin: "18px 0 8px",
        }}>
          // SHARE
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          <Btn icon={Copy}        onClick={() => copy(card?.callsign || "", "Callsign")} testid="copy-callsign">COPY CALLSIGN</Btn>
          <Btn icon={LinkIcon}    onClick={() => publicUrl ? copy(publicUrl, "Profile link") : toast.error("Enable Public Profile first")} testid="copy-link">COPY LINK</Btn>
          <Btn icon={Twitter}     onClick={() => xUrl ? shareIntent(xUrl) : toast.error("Enable Public Profile first")} testid="share-x">SHARE TO X</Btn>
          <Btn icon={MessageCircle} onClick={() => redditUrl ? shareIntent(redditUrl) : toast.error("Enable Public Profile first")} testid="share-reddit">SHARE TO REDDIT</Btn>
          <Btn icon={AtSign}      onClick={() => bskyUrl ? shareIntent(bskyUrl) : toast.error("Enable Public Profile first")} testid="share-bsky">SHARE TO BLUESKY</Btn>
        </div>

        {!publicUrl && (
          <div style={{
            marginTop: 12, padding: 12,
            border: "1px dashed #454038",
            fontFamily: "ui-monospace, monospace", fontSize: 10, letterSpacing: "0.05em",
            color: "#A39E93",
          }} data-testid="public-profile-disabled-notice">
            Share links require <strong>Public Profile</strong> to be enabled in Card Settings.
            The card itself is always private-downloadable.
          </div>
        )}
      </div>

      {/* Offscreen wallpaper canvases */}
      <div style={{ position: "fixed", left: -99999, top: -99999, pointerEvents: "none" }} aria-hidden>
        <div
          ref={phoneRef}
          style={{
            width: 1170, height: 2532,                       /* iPhone 13 Pro */
            background: "linear-gradient(180deg, #08070A, #03020A)",
            display: "flex", alignItems: "center", justifyContent: "center",
            position: "relative", overflow: "hidden",
          }}
        >
          <div style={{ transform: "scale(1.4)" }}>
            <OperativeCard card={card} qrUrl={publicUrl} hideTestId />
          </div>
        </div>
        <div
          ref={desktopRef}
          style={{
            width: 2560, height: 1440,
            background: "linear-gradient(135deg, #08070A, #03020A)",
            display: "flex", alignItems: "center", justifyContent: "center",
            position: "relative", overflow: "hidden",
          }}
        >
          <div style={{ transform: "scale(1.2)" }}>
            <OperativeCard card={card} qrUrl={publicUrl} hideTestId />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CardActions;
